import { createFileRoute, notFound, redirect, Link } from "@tanstack/react-router";
import { Section } from "@/components/layout/Page";
import { getCurrentLocale } from "@/lib/utils/locale";
import { BLOG_POSTS, getLocalizedPost, loadLocalizedPost } from "@/lib/mock/blog";
import { authorBio, authorProfileUrl } from "@/lib/mock/authors";
import { activeDict } from "@/lib/i18n-dict";
import { breadcrumbSchema, jsonLdScript } from "@/lib/utils/schema-lists";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Share2,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  Sparkles,
  ArrowRight,
  BookOpen,
  User,
  Clock,
} from "lucide-react";
import type { BlogPost } from "@/lib/types";
import { PRODUCTS } from "@/lib/mock/products";
import { ProductCard } from "@/components/ProductCard";
import { useI18n } from "@/context/I18nContext";
import { MarkdownRenderer } from "@/components/ui/MarkdownRenderer";
import { EventPhotoGallery } from "@/components/EventPhotoGallery";
import { GlossaryTermsBlock } from "@/components/GlossaryTermsBlock";
import { EVENT_MEDIA } from "@/lib/mock/event-media";
import { absoluteAssetUrl, localizedUrl } from "@/lib/utils/site-url";
import { ORGANIZATION_ID } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { publishNotFound } from "@/lib/utils/canonical-override";
import { snippetDescription, snippetTitle } from "@/lib/utils/seo";

export const Route = createFileRoute("/blog/$category/$slug")({
  // Async because the article body for a non-English locale is a dynamic
  // import — see loadLocalizedPost(). The resolved post travels in loader data,
  // so the component never re-resolves the body at render time.
  loader: async ({ params }) => {
    // Pass the locale: head() builds the <title>, description, og: tags and
    // BlogPosting JSON-LD from loaderData, so without it every locale's
    // metadata stayed English even where the visible article was translated.
    const post = await loadLocalizedPost(params.slug, getCurrentLocale());
    if (!post) {
      publishNotFound(
        `/blog/${encodeURIComponent(params.category)}/${encodeURIComponent(params.slug)}`,
      );
      throw notFound();
    }
    // getPost matches on the slug alone, so /blog/<any-string>/<valid-slug>
    // used to render the article at 200 — an unbounded set of duplicate URLs,
    // in all ten locales, each self-canonicalising. The JSON-LD below already
    // built its @id from post.categorySlug, so those URLs also shipped a
    // canonical tag and a JSON-LD url that disagreed with each other.
    if (params.category !== post.categorySlug) {
      // Server-side the locale prefix must be re-added (server.ts stripped it
      // before the router ran); in the browser createHref re-applies it, so
      // adding it here would double it. Same rule as shop.$slug.
      const lang = getCurrentLocale();
      const prefix = typeof window === "undefined" && lang !== "en" ? `/${lang}` : "";
      throw redirect({
        statusCode: 301,
        href: `${prefix}/blog/${post.categorySlug}/${post.slug}`,
        replace: true,
      });
    }
    return { post };
  },
  head: ({ loaderData }) => {
    if (!loaderData?.post) return {};
    const dict = activeDict();
    const post = loaderData.post;
    // Locale-prefixed, via the shared helper. These @ids used to be built from
    // the bare site URL, so a Turkish article told Google the ENGLISH URL was
    // the page being described — contradicting that page's own canonical, in
    // all eight non-English locales.
    const pageUrl = localizedUrl(`/blog/${post.categorySlug}/${post.slug}`);
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${pageUrl}#article`,
      url: pageUrl,
      inLanguage: getCurrentLocale(),
      headline: post.title,
      description: post.excerpt,
      image: post.image ? absoluteAssetUrl(post.image) : undefined,
      datePublished: post.date,
      // Emitted only where BlogPost.updated is set. There is a field behind it
      // now, but defaulting it to `date` would assert that every article was
      // last edited on its publish day — so an unrevised post still carries no
      // dateModified rather than a manufactured one.
      dateModified: post.updated,
      // No jobTitle. It was a ternary on the initials, so every author who was
      // not VA became "Logistics Director" — including Reza R., whose visible
      // bio on this same page calls him a Project Manager. A structured-data
      // claim that contradicts the rendered page is worse than an absent
      // optional field, and nothing in the post record carries a real title.
      author: {
        "@type": "Person",
        name: post.author,
        // seo_master_plan P1.5's stable author URL, emitted only for the
        // authors who actually have a public profile — a fabricated or
        // on-site-only author URL identifies nothing.
        url: authorProfileUrl(post.authorInitials),
        worksFor: { "@id": ORGANIZATION_ID },
      },
      // Reference the one company node rather than restating it, so Google
      // resolves a single entity instead of several similar ones.
      publisher: { "@id": ORGANIZATION_ID },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": pageUrl,
      },
    };
    // Drops the " | Aqua MAG" suffix when the headline alone already fills the
    // 60-character budget, rather than letting Google cut the brand off itself.
    const metaTitle = snippetTitle(post.title, " | Aqua MAG");
    const metaDescription = snippetDescription(post.excerpt);
    return {
      meta: [
        { title: metaTitle },
        { name: "description", content: metaDescription },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: metaDescription },
        { property: "og:type", content: "article" },
        { property: "article:published_time", content: post.date },
        { property: "article:author", content: post.author },
        ...(post.image
          ? [
              { property: "og:image", content: absoluteAssetUrl(post.image) },
              { name: "twitter:image", content: absoluteAssetUrl(post.image) },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: metaTitle },
        { name: "twitter:description", content: metaDescription },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
        // Home > Aqua MAG > Category > Article, matching the trail rendered
        // above the headline.
        jsonLdScript(
          "ld-breadcrumb",
          breadcrumbSchema([
            { name: dict["nav.home"] ?? "Home", path: "/" },
            { name: dict["blog.journalName"] ?? "Aqua MAG", path: "/blog" },
            {
              name: dict[`blog.categories.${post.categorySlug}`] ?? post.category,
              path: `/blog/${post.categorySlug}`,
            },
            { name: post.title },
          ]),
        ),
      ],
    };
  },
  component: Article,
});

function Article() {
  const { post: rawPost } = Route.useLoaderData() as { post: BlogPost };
  const { t, lang } = useI18n();
  // rawPost is loader data and already localized, body included. Re-applying
  // getLocalizedPost keeps the title/excerpt correct after a client-side locale
  // switch; translations no longer carry bodies, so its `translation.body ||
  // post.body` fallback preserves the body the loader resolved.
  const post = getLocalizedPost(rawPost, lang);
  const articlePhotos = post.eventId
    ? EVENT_MEDIA[post.eventId]?.photos.filter((photo) => photo.articleGallery !== false)
    : undefined;

  // Find related articles in same category, or fallback to latest posts
  const localizedPosts = BLOG_POSTS.map((p) => getLocalizedPost(p, lang));
  let relatedPosts = localizedPosts.filter(
    (p) => p.categorySlug === post.categorySlug && p.slug !== post.slug,
  );
  if (relatedPosts.length < 3) {
    const extraPosts = localizedPosts.filter(
      (p) => p.slug !== post.slug && !relatedPosts.some((r) => r.slug === p.slug),
    );
    relatedPosts = [...relatedPosts, ...extraPosts].slice(0, 3);
  }

  // Social Share event handlers
  const handleShareTwitter = () => {
    if (typeof window !== "undefined") {
      window.open(
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`,
        "_blank",
      );
    }
  };

  const handleShareLinkedIn = () => {
    if (typeof window !== "undefined") {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
        "_blank",
      );
    }
  };

  const handleCopyLink = () => {
    if (typeof window !== "undefined") {
      navigator.clipboard.writeText(window.location.href);
      toast.success(t("blog.linkCopied"));
    }
  };

  /**
   * End-of-article CTA, chosen by category.
   *
   * Was three near-identical blocks of hardcoded English, so every locale read
   * an English call to action under a translated article. Now one block driven
   * by a key prefix.
   *
   * The live-feed headline used to read "Optimize Hatchery Live Feed Yields Up
   * to 8x". The 8× figure is real but it belongs to rotifer biomass over three
   * days — the number every other surface on this site states that way
   * (projects.case1.highlight, product.super-fresh-chlorella-v12.faq.1.a).
   * Generalising it to "live feed yields" widened the claim, and translating
   * the wider version into eight languages would have entrenched it, so the
   * headline states what the trials actually measured.
   */
  const CTA_VARIANTS = {
    liveFeed: {
      categories: ["feed-nutrition", "feed-innovation-sustainability-8", "hatchery-feed-design-6"],
      primary: { to: "/contactus", search: { inquiry: "live-feed" } },
      secondary: { to: "/products", search: { category: "live-feed-aquaculture" } },
    },
    salmonOva: {
      categories: ["hatchery-solutions", "aquaculture-10"],
      primary: { to: "/contactus", search: { inquiry: "salmon-ova" } },
      secondary: { to: "/products", search: { category: "hatchery-solutions" } },
    },
    export: {
      categories: [],
      primary: { to: "/request-quote", search: {} },
      secondary: { to: "/contactus", search: {} },
    },
  } as const;

  const ctaKey =
    (Object.keys(CTA_VARIANTS) as (keyof typeof CTA_VARIANTS)[]).find((key) =>
      (CTA_VARIANTS[key].categories as readonly string[]).includes(post.categorySlug),
    ) ?? "export";
  const cta = CTA_VARIANTS[ctaKey];

  const getCtaBlock = () => (
    <div className="glass-panel-dark rounded-3xl border border-mint/20 p-8 relative overflow-hidden shadow-2xl my-10">
      <div className="absolute top-0 right-0 w-36 h-36 bg-mint/10 rounded-full blur-2xl pointer-events-none" />
      <div className="space-y-4 max-w-xl relative">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-mint text-navy text-[10px] font-bold uppercase tracking-widest">
          {t(`blog.cta.${ctaKey}.eyebrow`)}
        </div>
        <h3 className="font-display text-2xl font-bold text-white leading-tight">
          {t(`blog.cta.${ctaKey}.title`)}
        </h3>
        <p className="text-sm text-white/80 leading-relaxed">{t(`blog.cta.${ctaKey}.body`)}</p>
        <div className="pt-2 flex flex-wrap gap-4">
          <Button asChild className="bg-mint text-navy hover:bg-mint/90 font-bold rounded-xl px-6">
            <Link to={cta.primary.to} search={cta.primary.search}>
              {t(`blog.cta.${ctaKey}.primary`)}
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="border-white/20 hover:bg-white/10 text-white rounded-xl px-6"
          >
            <Link to={cta.secondary.to} search={cta.secondary.search}>
              {t(`blog.cta.${ctaKey}.secondary`)}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );

  // Author bio for authority signals (E-A-T), from the shared record in
  // lib/mock/authors.ts. This used to be a second copy of the bios that had
  // drifted from the one on /blog, and its fallback handed an unrecognised
  // author Reza's biography rather than showing none.
  const bio = authorBio(post.authorInitials);

  return (
    <>
      <section className="border-b bg-surface-alt">
        <div className="mx-auto max-w-5xl px-4 py-12 md:px-6 md:py-16">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6 font-semibold overflow-x-auto whitespace-nowrap py-1">
            <Link to="/" className="hover:text-primary transition-colors">
              {t("nav.home")}
            </Link>
            <span>/</span>
            <Link to="/blog" className="hover:text-primary transition-colors">
              {t("nav.blog")}
            </Link>
            <span>/</span>
            <Link
              to="/blog/$category"
              params={{ category: post.categorySlug }}
              className="hover:text-primary transition-colors uppercase tracking-wider"
            >
              {post.category}
            </Link>
            <span>/</span>
            <span className="text-navy truncate max-w-[200px]">{post.title}</span>
          </nav>

          <h1 className="mt-4 font-display text-4xl font-bold leading-tight text-navy md:text-5xl">
            {post.title}
          </h1>
          <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-navy text-xs text-white">
                {post.authorInitials}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium text-navy">{post.author}</span>
            <span>·</span>
            <span>
              {new Date(post.date).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
            <span>·</span>
            <span>
              {post.readMinutes} {t("blog.readMinutesSuffix")}
            </span>
          </div>
        </div>
      </section>

      {post.image && (
        <div className="mx-auto max-w-5xl -mt-8 px-4 md:px-6">
          <img
            src={post.image}
            alt={post.title}
            className="aspect-[16/9] w-full rounded-3xl border object-cover shadow-lg"
          />
        </div>
      )}

      <Section>
        <div className="mx-auto max-w-5xl grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Main Content Column */}
          <div className="lg:col-span-8 space-y-8">
            <article className="prose prose-neutral max-w-none text-base leading-relaxed">
              <MarkdownRenderer content={post.body} />

              {articlePhotos && articlePhotos.length > 0 && (
                <section
                  aria-labelledby="event-gallery-heading"
                  className="not-prose space-y-5 pt-4"
                >
                  <h2
                    id="event-gallery-heading"
                    className="font-display text-2xl font-bold text-navy"
                  >
                    {t("events.recap.heading")}
                  </h2>
                  <EventPhotoGallery
                    photos={articlePhotos.map((photo) => ({
                      src: photo.src,
                      alt: t(photo.captionKey),
                    }))}
                  />
                </section>
              )}
            </article>

            <GlossaryTermsBlock text={post.body} />

            {/* Custom Inbound CTA Block */}
            {getCtaBlock()}

            {/* Social Share widget */}
            <div className="mt-12 flex items-center gap-3 border-t pt-6 text-sm text-muted-foreground">
              <Share2 className="h-4 w-4" /> {t("blog.share")}
              <button
                onClick={handleShareTwitter}
                className="rounded p-1 hover:bg-muted cursor-pointer transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-4 w-4" />
              </button>
              <button
                onClick={handleShareLinkedIn}
                className="rounded p-1 hover:bg-muted cursor-pointer transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-4 w-4" />
              </button>
              <button
                onClick={handleCopyLink}
                className="rounded p-1 hover:bg-muted cursor-pointer transition-colors"
                aria-label="Copy link"
              >
                <LinkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Sidebar Column */}
          <aside className="lg:col-span-4 space-y-8">
            {/* E-A-T Author Bio Card */}
            <div className="glass-card rounded-3xl p-6 border border-border/80 bg-surface-alt/30 space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-border/60">
                <User className="h-4 w-4 text-primary" />
                <h3 className="font-display text-sm font-bold text-navy">
                  {t("blog.aboutTheAuthor")}
                </h3>
              </div>
              <div className="flex gap-3 items-start">
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarFallback className="bg-navy text-xs text-white">
                    {post.authorInitials}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h4 className="text-xs font-bold text-navy">{post.author}</h4>
                </div>
              </div>
              {bio && <p className="text-xs text-muted-foreground leading-relaxed">{bio}</p>}
            </div>

            {/* Related Articles Card */}
            <div className="glass-card rounded-3xl p-6 border border-border/80 bg-surface-alt/30 space-y-4">
              <div className="flex items-center gap-2 pb-3 border-b border-border/60">
                <BookOpen className="h-4 w-4 text-primary" />
                <h3 className="font-display text-sm font-bold text-navy">
                  {t("blog.relatedArticles")}
                </h3>
              </div>
              <div className="space-y-4">
                {relatedPosts.map((r) => (
                  <Link
                    key={r.slug}
                    to="/blog/$category/$slug"
                    params={{ category: r.categorySlug, slug: r.slug }}
                    className="group block space-y-1"
                  >
                    <span className="text-[10px] text-primary uppercase font-bold tracking-wider">
                      {r.category}
                    </span>
                    <h4 className="font-display text-xs font-bold text-navy group-hover:text-primary transition-colors leading-snug">
                      {r.title}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </Section>

      {/* Related Products Section in Magazine Article */}
      <Section band className="border-t border-border/80 bg-surface-alt/40 py-12 lg:py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div className="space-y-2 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                <span>Featured Products Mentioned</span>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-navy tracking-tight">
                Featured Aqua Inputs & Export Solutions
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Explore premium live feed concentrates, salmonid ova, and certified seafood exports
                referenced in this article.
              </p>
            </div>

            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-xs font-bold text-primary hover:text-navy transition-colors shrink-0 group"
            >
              <span>Explore Product Catalog</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(() => {
              const matched = PRODUCTS.filter((p) => {
                if (post.slug.includes("chlorella") || post.slug.includes("sv12")) {
                  return [
                    "super-fresh-chlorella-v12",
                    "emerald-chlorella-powder",
                    "revive-artemia-cysts-2kg",
                  ].includes(p.slug);
                }
                if (post.slug.includes("artemia")) {
                  return [
                    "revive-artemia-cysts-2kg",
                    "revive-decapsulated-artemia",
                    "super-fresh-chlorella-v12",
                  ].includes(p.slug);
                }
                if (post.slug.includes("salmon") || post.slug.includes("ova")) {
                  return [
                    "atlantic-salmon-ova",
                    "triploid-all-female-salmon-ova",
                    "vital-wheat-gluten-82",
                  ].includes(p.slug);
                }
                if (
                  post.slug.includes("export") ||
                  post.slug.includes("seafood") ||
                  post.slug.includes("middle-east")
                ) {
                  return ["mediterranean-sea-bass", "mediterranean-sea-bream", "meagre"].includes(
                    p.slug,
                  );
                }
                return false;
              });
              const displayList = matched.length > 0 ? matched : PRODUCTS.slice(0, 3);
              return displayList.map((product) => (
                <ProductCard key={product.slug} product={product} />
              ));
            })()}
          </div>
        </div>
      </Section>
    </>
  );
}
